import { enqueue, peekAll, queueSize, type QueuedSubmission } from './db';
import { flushScans } from './idscans';
import { geolocation } from '../../platform/geolocation';
import { keepSessionAlive } from '../api/session';
import { flushQueue, type FlushResult } from './flush';
import type { GeoFix } from '../../platform/types';
import type { SkipReason } from '../domain/types';
import { reportQueue } from '../telemetry/diag';
import { stopStep } from '../telemetry/stopLog';
import { reportError } from '../telemetry/report';

/**
 * Page-side sync orchestration (the "parking lot flow").
 *
 * Submission strategy for Complete/Skip:
 *   1. Always enqueue to IndexedDB first (crash-safe).
 *   2. Try an immediate flush — online case resolves in-line.
 *   3. If offline, register Background Sync; also retry on 'online' and
 *      visibilitychange (covers iOS which lacks Background Sync).
 */

type Listener = (pending: number, lastResult: FlushResult | null) => void;
const listeners = new Set<Listener>();
let lastResult: FlushResult | null = null;

export function onQueueChange(fn: Listener): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

async function notify(): Promise<void> {
  const n = await queueSize();
  listeners.forEach((fn) => fn(n, lastResult));
}

async function registerBackgroundSync(): Promise<void> {
  try {
    const reg = await navigator.serviceWorker.ready;
    // @ts-expect-error SyncManager is not yet in all TS lib.dom versions
    if (reg.sync) await reg.sync.register('jw-completions');
  } catch { /* unsupported (iOS) — page-side retries cover it */ }
}

/**
 * Attach a GPS fix to anything waiting for one.
 *
 * A delivery captured where there is no signal is held rather than destroyed
 * (see flush.ts). It cannot be sent without a position, so as soon as the phone
 * has one — which in practice is the moment the driver walks back to the van —
 * it is given one and goes.
 *
 * Only ever ADDS a fix to an item that has none. An existing fix is the one
 * taken at the stop and is never overwritten.
 */
async function attachMissingFixes(): Promise<void> {
  try {
    const waiting = (await peekAll()).filter((i) => i.kind === 'complete' && !i.fix);
    if (waiting.length === 0) return;

    let fix = geolocation.getLastKnownFix(300_000);
    if (!fix) {
      try { fix = await geolocation.getFreshFix(8_000); } catch { /* still blind */ }
    }
    if (!fix) return;                 // nothing to give yet; try again next time

    for (const item of waiting) {
      await enqueue({ ...item, fix });
    }
  } catch {
    /* Housekeeping. It must never be the reason a flush does not happen. */
  }
}

export async function trySync(): Promise<FlushResult> {
  await attachMissingFixes();
  // Face scans taken without signal go FIRST (134): the office checks that a
  // stop's scan has arrived before it accepts the delivery.
  await flushScans();
  lastResult = await flushQueue();
  // Diagnostics only (3 Oct): what is still waiting on the phone, and for how long.
  if (lastResult.remaining > 0) void reportQueue('sync');
  await notify();
  /*
   * Nothing queued can be sent until the driver signs in, and until yesterday
   * nothing told them so — the queue just retried behind a screen that looked
   * fine. Send them to the login screen once, from wherever they are.
   *
   * Their work is not lost: the queue and the stop drafts both survive signing
   * in again, and the next flush sends everything.
   */
  if (lastResult.authExpired && !/^\/(login|engine-check)/.test(location.pathname)) {
    location.assign('/login?expired=1');
  } else if (lastResult.needsScan?.length && !location.pathname.startsWith('/stop/')) {
    // A delivery is waiting for its face scan: open that stop's scan (134).
    location.assign(`/stop/${lastResult.needsScan[0]}`);
  }
  return lastResult;
}

/** A captured picture the phone could not read back. The driver retakes it. */
export class ProofUnreadable extends Error {
  constructor(public which: 'photo' | 'signature') { super(`proof_unreadable:${which}`); }
}

async function readBytes(b: Blob): Promise<ArrayBuffer | null> {
  const ok = (buf: ArrayBuffer | null | undefined) => (buf && buf.byteLength > 0 ? buf : null);
  try { const v = ok(await b.arrayBuffer()); if (v) return v; } catch { /* next way */ }
  try { const v = ok(await new Response(b).arrayBuffer()); if (v) return v; } catch { /* next way */ }
  try {
    const v = ok(await new Promise<ArrayBuffer | null>((resolve) => {
      const r = new FileReader();
      r.onload = () => resolve(r.result instanceof ArrayBuffer ? r.result : null);
      r.onerror = () => resolve(null);
      r.readAsArrayBuffer(b);
    }));
    if (v) return v;
  } catch { /* nothing left to try */ }
  return null;
}

export async function submitCompletion(args: {
  taskId: string; fix: GeoFix; photoBlob: Blob;
  signatureBlob: Blob | null; recipientName: string | null; barcodes: string[];
  barcodeException?: string | null;
  totesReturned?: number | null;
}): Promise<FlushResult> {
  /*
   * Bytes, not Blobs, go into storage (see QueuedSubmission.photoBytes), read
   * HERE, while the driver is still standing at the stop.
   *
   * 10 Oct (Temessa Guzman, Mercy San Juan): reading the signature failed, the
   * old code quietly kept the Blob instead, the iPhone threw that Blob away
   * while the app sat in the background, and the delivery could never be sent
   * or redone. Now a picture that cannot be read stops the save and the
   * driver is asked to take it again, on the spot. Three ways of reading are
   * tried first, because Safari sometimes fails one and not the others.
   */
  const photoBytes = await readBytes(args.photoBlob);
  const signatureBytes = args.signatureBlob ? await readBytes(args.signatureBlob) : null;
  if (!photoBytes || (args.signatureBlob && !signatureBytes)) {
    const which = !photoBytes ? 'photo' : 'signature';
    reportError('diag_sync', 'sync', { ok: false, stage: `read_${which}`, error: 'proof_unreadable' });
    stopStep(args.taskId, `proof_unreadable:${which}`);
    throw new ProofUnreadable(which);
  }
  const item: QueuedSubmission = {
    id: crypto.randomUUID(),
    kind: 'complete',
    taskId: args.taskId,
    queuedAt: Date.now(),
    fix: args.fix,
    recipientName: args.recipientName,
    barcodes: args.barcodes,
    barcodeException: args.barcodeException ?? null,
    totesReturned: args.totesReturned ?? null,
    photoBlob: null,
    signatureBlob: null,
    photoBytes,
    photoType: args.photoBlob.type || 'image/jpeg',
    signatureBytes,
    signatureType: args.signatureBlob?.type || 'image/jpeg',
    skipReason: null,
    skipNote: null,
    attempts: 0,
    lastError: null,
  };
  await enqueue(item);
  stopStep(args.taskId, 'saved_on_phone');
  await registerBackgroundSync();
  return trySync();
}

export async function submitSkip(args: {
  taskId: string; reason: SkipReason; fix: GeoFix | null; note: string | null;
}): Promise<FlushResult> {
  await enqueue({
    id: crypto.randomUUID(),
    kind: 'skip',
    taskId: args.taskId,
    queuedAt: Date.now(),
    fix: args.fix,
    recipientName: null,
    barcodes: [],
    barcodeException: null,
    photoBlob: null,
    signatureBlob: null,
    skipReason: args.reason,
    skipNote: args.note,
    attempts: 0,
    lastError: null,
  });
  stopStep(args.taskId, 'skip_saved_on_phone');
  await registerBackgroundSync();
  return trySync();
}

/** Call once at app startup. */
export function initSync(): void {
  /*
   * Renew the session while the driver is working.
   *
   * Hooked to the same three moments the queue already uses — startup, signal
   * returning, and the app coming back to the foreground — because those are
   * exactly when a driver is active. Nothing new is polled and no timer runs;
   * the call does nothing at all until the session is past halfway.
   */
  window.addEventListener('online', () => { void keepSessionAlive(); void trySync(); });
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') {
      void keepSessionAlive();
      void trySync();
    }
  });
  navigator.serviceWorker?.addEventListener('message', (e) => {
    const type = (e.data as { type?: string })?.type;
    if (type === 'queue-flushed') void notify();
    // The worker found the session dead while the page sat idle.
    if (type === 'auth-expired' && !/^\/(login|engine-check)/.test(location.pathname)) {
      location.assign('/login?expired=1');
    }
  });
  void keepSessionAlive();
  void trySync(); // drain anything left from a previous session
  /*
   * 2 Nov: keep trying while something is waiting and the app is open. A send
   * that failed with signal (Abdul's photo, "Load failed", four times on 28 Oct)
   * was only tried again when the app was reopened or signal came back, so a
   * driver with the app open could wait half an hour. Every 30 seconds now,
   * only while the screen is on and something is waiting.
   */
  setInterval(() => {
    if (document.visibilityState !== 'visible') return;
    void queueSize().then((n) => { if (n > 0) void trySync(); }).catch(() => undefined);
  }, 30_000);
}
